import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  CustomSelect,
  HeaderInput,
  HeaderTemplate,
  Progress,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import { RichTextField } from 'web-library/stories/atom/RichTextField/RichTextField';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useMediaQuery, useTheme } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import AnnouncementNotificationTitleTextField
  from 'web-library/components/AnnouncementNotificationTitleTextField';
import {
  generateAuthorityName,
  NOTIFICATION_FUNCTION_MAP,
} from 'web-library/models/AuthorityFunctions';
import {
  FunctionModule,
  PermissionType,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { configurationTypeData } from '../../modules/admin/logic/dropDownOption';
import {
  useGetNotificationFeature
} from '../../api-refactored/manage-notifications/useGetNotificationFeature';
import {
  usePostCreateNotificationTemplate
} from '../../api-refactored/manage-notifications/usePostCreateNotificationTemplate';
import RouteKey from '../../routes/key';
import {
  useGetNotificationTemplateDetails
} from '../../api-refactored/manage-notifications/useGetNotificationTemplateDetails';
import { usePutEditTemplate } from '../../api-refactored/manage-notifications/usePutEditTemplate';
import createEditTemplateSchema, { CreateEditTemplateData } from './createEditTemplateSchema';
import { useGetUserAuthorities } from '../../api-refactored/user/useGetUserAuthorities';

const useStyles = makeStyles()((theme) =>
({
  select: {
    marginBottom: theme.spacing(1.5),
  },
  textFieldContainer: {
    marginBottom: theme.spacing(6),
  },
  notificationTextField: {
    marginBottom: theme.spacing(4),
  },
  cancelButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(3),
    },
  },
  buttonContainer: {
    marginBottom: theme.spacing(7),
  },
  errorContainer: {
    marginBottom: theme.spacing(2),
  },
  errorText: {
    color: theme.color.state.stateError,
  },
}));

const { admin } = RouteKey.child;
const { manageNotifications } = RouteKey.admin.child;

interface CreateEditTemplateProps {
  isEdit: boolean;
}

const CreateEditTemplate = ({ isEdit }: CreateEditTemplateProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const history = useHistory<{
    featureValue: string;
    defaultTab: number;
    page?: number;
    pageSize?: number;
  }>();
  const { uuid } = useParams<{ uuid: string }>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const dataFromListing = history?.location?.state;

  const [featureValue, setFeatureValue] = useState<string>(dataFromListing.featureValue || '');

  const [titleText, setTitleText] = useState<string>('');

  const [descriptionText, setDescriptionText] = useState<string>('');

  const [displayDescriptionError, setDisplayDescriptionError] = useState<boolean>(false);

  const [errMsg, setErrMsg] = useState<string>('');

  const [lastUpdatedAt, setLastUpdatedAt] = useState<string>('');

  const [lastUpdatedBy, setLastUpdatedBy] = useState<string>('');

  const [trimmedDescLength, setTrimmedDescLength] = useState<number>(0);

  const [isFetchedTemplateData, setIsFetchTemplateData] = useState<boolean>(false);

  const authorities = useGetUserAuthorities();

  const formHook = useForm<CreateEditTemplateData>({
    mode: 'onSubmit',
    resolver: joiResolver(createEditTemplateSchema(featureValue === 'SMS')),
    defaultValues: {
      announcementTitle: titleText,
    },
  });

  const handleBackToListing = (isCreateTemplate: boolean) =>
    history.push(`/${admin}/${manageNotifications.route}`, {
      featureValue,
      defaultTab: isCreateTemplate ? 1 : dataFromListing.defaultTab,
      page: dataFromListing.page,
      pageSize: dataFromListing.pageSize,
    });

  const { data: notificationFeature, isLoading: isLoadingNotificationFeature } =
    useGetNotificationFeature();

  const { isLoading: isLoadingNotificationTemplateDetails } = useGetNotificationTemplateDetails({
    onSuccess: (res) => {
      setTitleText(res.title);
      formHook.reset({
        announcementTitle: res.title,
      });
      setDescriptionText(res.message);
      if (featureValue !== 'SMS') {
        setTrimmedDescLength(res.message.trim().length);
      }
      setFeatureValue(res.templateType);
      setLastUpdatedAt(res.lastUpdatedAt);
      setLastUpdatedBy(res.lastUpdatedBy);
      setIsFetchTemplateData(true);
    },
    uuid,
    enabled: isEdit,
  });

  const { mutate: mutatePostCreateTemplate, status: statusPostCreateTemplate } =
    usePostCreateNotificationTemplate({
      onSuccess: () => handleBackToListing(true),
      onError: (err) => {
        setErrMsg(err?.response?.data?.message!);
      },
    });

  const { mutate: mutatePutEditTemplate, status: statusPutEditTemplate } = usePutEditTemplate({
    onSuccess: () => handleBackToListing(true),
    onError: (err) => {
      setErrMsg(err?.response?.data?.message!);
    },
    uuid,
  });

  if (
    isLoadingNotificationFeature ||
    isLoadingNotificationTemplateDetails ||
    (featureValue !== 'SMS' && !descriptionText.length && isEdit && !isFetchedTemplateData)
  ) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <form
      onSubmit={formHook.handleSubmit(() => {
        if (isEdit && !displayDescriptionError) {
          mutatePutEditTemplate({
            title: titleText,
            message: featureValue !== 'SMS' ? descriptionText : null,
            templateType: featureValue,
            lastUpdatedAt,
            lastUpdatedBy,
          });
        } else if (!displayDescriptionError) {
          mutatePostCreateTemplate({
            title: titleText,
            message: featureValue !== 'SMS' ? descriptionText : null,
            templateType: featureValue,
          });
        }
      })}
    >
      <TitleHeader title={t('announcementsNotifications->>Listing.ManageNotifications')} />
      <HeaderTemplate>
        <HeaderInput
          label={t('announcementsNotifications->>Listing.Feature')}
          input={
            notificationFeature &&
            configurationTypeData(notificationFeature) && (
              <CustomSelect
                id="inputSelect-feature"
                isHeader
                menuItems={configurationTypeData(notificationFeature)}
                value={featureValue}
                className={classes.select}
                disabled
              />
            )
          }
          topBottom={false}
        />
      </HeaderTemplate>
      <ContentTemplate>
        <SectionHeader title={t('announcementsNotifications->>ViewDetails.NotificationDetails')} />
        <Box className={classes.textFieldContainer}>
          <Box className={featureValue !== 'SMS' ? classes.notificationTextField : ''}>
            <AnnouncementNotificationTitleTextField
              formHook={formHook}
              maxCharacterCount={160}
              label={
                featureValue !== 'SMS'
                  ? t('announcementsNotifications->>CreateTemplate.Title')
                  : t('announcementsNotifications->>CreateTemplate.Message')
              }
              placeholder={
                featureValue !== 'SMS'
                  ? t('announcementsNotifications->>CreateTemplate.TitlePlaceholder')
                  : t('announcementsNotifications->>CreateTemplate.MessagePlaceholder')
              }
              featureValue={featureValue}
              titleText={titleText}
              setTitleText={setTitleText}
            />
            {!!formHook.formState.errors.announcementTitle && (
              <Typography variant="body2" color="error">
                {formHook.formState.errors.announcementTitle.message}
              </Typography>
            )}
          </Box>
          {featureValue !== 'SMS' && (
            <RichTextField
              textValue={descriptionText}
              setTextValue={setDescriptionText}
              displayErrorOnEmpty={displayDescriptionError}
              label={t('announcementsNotifications->>CreateTemplate.Message')}
              maxCharacterCount={featureValue === 'EMAIL' ? 10000 : 500}
              placeholder={t('announcementsNotifications->>CreateTemplate.MessagePlaceholder')}
              isRequired={featureValue !== 'SMS'}
              placeholderMenu
              setTrimmedCharLength={setTrimmedDescLength}
            />
          )}
        </Box>
        {errMsg && (
          <Box className={classes.errorContainer}>
            <Typography className={classes.errorText} display="inline" variant="body2">
              {errMsg}
            </Typography>
          </Box>
        )}
        <Box className={classes.buttonContainer}>
          <Button
            id="button-create-template-cancel"
            className={classes.cancelButton}
            variant="outlined"
            fullWidth={isMobile}
            onClick={() => handleBackToListing(false)}
          >
            {t('announcementsNotifications->>CreateTemplate.Cancel')}
          </Button>

          {authorities.data?.includes(
            generateAuthorityName(
              'CP',
              FunctionModule.ADMIN,
              NOTIFICATION_FUNCTION_MAP[featureValue],
              isEdit ? PermissionType.EDIT : PermissionType.CREATE
            )
          ) && (
              <ApiButton
                id="button-create-template-create-template"
                type="submit"
                status={isEdit ? statusPutEditTemplate : statusPostCreateTemplate}
                fullWidth={isMobile}
                onClick={() => {
                  if (featureValue !== 'SMS') {
                    setDisplayDescriptionError(!trimmedDescLength);
                  }
                }}
              >
                {t('announcementsNotifications->>CreateTemplate.CreateTemplate')}
              </ApiButton>
            )}
        </Box>
      </ContentTemplate>
    </form>
  );
};

export default CreateEditTemplate;
